import React, { useEffect, useMemo, useState } from "react";
import apiService from "../../services/apiService";
import "./CreateUser.css";

const emptyForm = {
  Username: "",
  Password: "",
  FullName: "",
  RoleID: "",
  IsActive: 1,
};

const ITEMS_PER_PAGE = 10;

export default function CreateUser() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      setError("");
      const res = await apiService.getRoles();
      setRoles(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("FETCH ROLES ERROR:", err);
      setError(err.message || "Failed to load roles.");
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setError("");
      const res = await apiService.getUsers();
      setUsers(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("FETCH USERS ERROR:", err);
      setError(err.message || "Failed to load users.");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter]);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return users.filter((u) => {
      const matchesSearch =
        !keyword ||
        String(u.UserID).toLowerCase().includes(keyword) ||
        String(u.Username || "").toLowerCase().includes(keyword) ||
        String(u.FullName || "").toLowerCase().includes(keyword) ||
        String(u.RoleName || "").toLowerCase().includes(keyword) ||
        String(u.RoleCode || "").toLowerCase().includes(keyword);

      const matchesRole =
        !roleFilter || String(u.RoleID) === String(roleFilter);

      return matchesSearch && matchesRole;
    });
  }, [users, search, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, currentPage]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "IsActive" ? Number(value) : value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const openAddModal = () => {
    setMessage("");
    setError("");
    resetForm();
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    resetForm();
    setShowAddModal(false);
  };

  const openEditModal = (user) => {
    setMessage("");
    setError("");
    setSelectedUser(user);
    setForm({
      Username: user.Username || "",
      Password: "",
      FullName: user.FullName || "",
      RoleID: user.RoleID ? String(user.RoleID) : "",
      IsActive: Number(user.IsActive) === 1 ? 1 : 0,
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setSelectedUser(null);
    resetForm();
    setShowEditModal(false);
  };

  const openDeleteModal = (user) => {
    setMessage("");
    setError("");
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedUser(null);
    setShowDeleteModal(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!form.Username || !form.Password || !form.FullName || !form.RoleID) {
      setError("Please fill in all fields.");
      return;
    }

    try {
      setSaving(true);

      await apiService.createUser({
        Username: form.Username.trim(),
        Password: form.Password,
        FullName: form.FullName.trim(),
        RoleID: Number(form.RoleID),
      });

      setMessage("User created successfully.");
      closeAddModal();
      await fetchUsers();
    } catch (err) {
      console.error("CREATE USER ERROR:", err);
      setError(err.message || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!selectedUser) {
      setError("No user selected.");
      return;
    }

    if (!form.Username || !form.FullName || !form.RoleID) {
      setError("Username, Full Name, and Role are required.");
      return;
    }

    try {
      setSaving(true);

      await apiService.updateUser(selectedUser.UserID, {
        Username: form.Username.trim(),
        Password: form.Password,
        FullName: form.FullName.trim(),
        RoleID: Number(form.RoleID),
        IsActive: Number(form.IsActive),
      });

      setMessage("User updated successfully.");
      closeEditModal();
      await fetchUsers();
    } catch (err) {
      console.error("UPDATE USER ERROR:", err);
      setError(err.message || "Failed to update user.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) {
      setError("No user selected.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      setError("");

      await apiService.deleteUser(selectedUser.UserID);

      setMessage("User deleted successfully.");
      closeDeleteModal();
      await fetchUsers();
    } catch (err) {
      console.error("DELETE USER ERROR:", err);
      setError(err.message || "Failed to delete user.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      setMessage("");
      setError("");
      await apiService.deactivateUser(userId);
      setMessage("User deactivated successfully.");
      await fetchUsers();
    } catch (err) {
      console.error("DEACTIVATE USER ERROR:", err);
      setError(err.message || "Failed to deactivate user.");
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const startItem = filteredUsers.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length);

  return (
    <div className="create-user-page">
      <div className="user-list-card full-width">
        <div className="page-header">
          <div>
            <h1>User Management</h1>
            <p className="subtitle">Manage system users, roles, and account status.</p>
          </div>

          <button className="btn btn-primary" onClick={openAddModal}>
            + Add User
          </button>
        </div>

        {message && <div className="alert success">{message}</div>}
        {error && <div className="alert error">{error}</div>}

        <div className="toolbar" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search by username, full name, role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="search-input"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ maxWidth: "240px" }}
          >
            <option value="">All Roles</option>
            {roles.map((role) => (
              <option key={role.RoleID} value={role.RoleID}>
                {role.RoleName} ({role.RoleCode})
              </option>
            ))}
          </select>
        </div>

        {loadingUsers ? (
          <p>Loading users...</p>
        ) : filteredUsers.length === 0 ? (
          <p>No users found.</p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th className="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((u) => (
                    <tr key={u.UserID}>
                      <td>{u.Username}</td>
                      <td>{u.FullName}</td>
                      <td>
                        {u.RoleName} ({u.RoleCode})
                      </td>
                      <td>
                        <span
                          className={
                            Number(u.IsActive) === 1 ? "status active" : "status inactive"
                          }
                        >
                          {Number(u.IsActive) === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="action-group">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEditModal(u)}
                          >
                            Edit
                          </button>

                          {Number(u.IsActive) === 1 && (
                            <button
                              type="button"
                              className="btn btn-warning btn-sm"
                              onClick={() => handleDeactivate(u.UserID)}
                            >
                              Deactivate
                            </button>
                          )}

                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => openDeleteModal(u)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className="pagination-wrap"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                marginTop: "16px",
              }}
            >
              <div className="pagination-info">
                Showing {startItem} to {endItem} of {filteredUsers.length} users
              </div>

              <div
                className="pagination-controls"
                style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>

                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`btn btn-sm ${
                      currentPage === page ? "btn-primary" : "btn-secondary"
                    }`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add User</h2>
              <button className="modal-close" onClick={closeAddModal}>
                ×
              </button>
            </div>

            <form className="create-user-form" onSubmit={handleCreate}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="Username"
                  value={form.Username}
                  onChange={handleChange}
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="Password"
                  value={form.Password}
                  onChange={handleChange}
                  placeholder="Enter password"
                />
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="FullName"
                  value={form.FullName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  name="RoleID"
                  value={form.RoleID}
                  onChange={handleChange}
                  disabled={loadingRoles}
                >
                  <option value="">
                    {loadingRoles ? "Loading roles..." : "Select role"}
                  </option>
                  {roles.map((role) => (
                    <option key={role.RoleID} value={role.RoleID}>
                      {role.RoleName} ({role.RoleCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={closeEditModal}>
                ×
              </button>
            </div>

            <form className="create-user-form" onSubmit={handleUpdate}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="Username"
                  value={form.Username}
                  onChange={handleChange}
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  name="Password"
                  value={form.Password}
                  onChange={handleChange}
                  placeholder="Leave blank if no change"
                />
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="FullName"
                  value={form.FullName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  name="RoleID"
                  value={form.RoleID}
                  onChange={handleChange}
                  disabled={loadingRoles}
                >
                  <option value="">
                    {loadingRoles ? "Loading roles..." : "Select role"}
                  </option>
                  {roles.map((role) => (
                    <option key={role.RoleID} value={role.RoleID}>
                      {role.RoleName} ({role.RoleCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="IsActive" value={form.IsActive} onChange={handleChange}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Updating..." : "Update"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-card modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete User</h2>
              <button className="modal-close" onClick={closeDeleteModal}>
                ×
              </button>
            </div>

            <div className="delete-content">
              <p>
                Are you sure you want to delete <strong>{selectedUser?.Username}</strong>?
              </p>
              <p className="delete-note">This action cannot be undone.</p>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? "Deleting..." : "Delete"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}